import { createContext, useEffect, useContext, useMemo, useCallback } from "react";
// hooks
import useLocalStorage from "../../hooks/useLocalStorage";
// utils
import localStorageAvailable from "../../utils/localStorageAvailable";
//
import { defaultSettings } from "./config-setting";
import { SettingsContextProps } from "./types";
import { defaultPreset, getPresets, presetsOption } from "./presets";

// ----------------------------------------------------------------------

const initialState: SettingsContextProps = {
  ...defaultSettings,
  // Layout
  onToggleLayout: () => {},
  onChangeLayout: () => {},
};

// ----------------------------------------------------------------------

export const SettingsContext = createContext(initialState);

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);

  if (!context) throw new Error("useSettingsContext must be use inside SettingsProvider");

  return context;
};

// ----------------------------------------------------------------------

type SettingsProviderProps = {
  children: React.ReactNode;
};

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useLocalStorage("settings", defaultSettings);

  // Layout
  const onToggleLayout = useCallback(() => {
    const themeLayout = settings.themeLayout === "vertical" ? "mini" : "vertical";
    setSettings({ ...settings, themeLayout });
  }, [setSettings, settings]);

  const onChangeLayout = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeLayout = event.target.value;
      setSettings({ ...settings, themeLayout });
    },
    [setSettings, settings]
  );

  const memoizedValue = useMemo(
    () => ({
      ...settings,
      // Layout
      onToggleLayout,
      onChangeLayout,
    }),
    [
      settings,
      // Layout
      onToggleLayout,
      onChangeLayout,
    ]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
